import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

interface CaseStudy {
  id: string;
  category: string;
  industry: string;
  gradient: string;
  companyIdentifier: string;
  headline: string;
  description: string;
  metrics: Array<{
    label: string;
    value: string;
  }>;
  testimonial: {
    quote: string;
    author: string;
  };
}

const caseStudies: CaseStudy[] = [
  {
    id: "manufacturing",
    category: "manufacturing",
    industry: "Manufacturing",
    gradient: "from-purple-500 to-violet-600",
    companyIdentifier: "Aluminium Products Manufacturer | 200+ employees",
    headline: "Screening 1000+ Candidates Per Month With 85% Less Time",
    description: "National aluminium products manufacturer needed to assess mechanical aptitude, process & industry knowledge, and language comprehension for production line & white collar roles across 3 facilities",
    metrics: [
      { label: "Time to hire", value: "33% reduction" },
      { label: "Cost per hire", value: "60% reduction" },
      { label: "Volume screened", value: "3.2x increase per recruiter" }
    ],
    testimonial: {
      quote: "We hire across multiple facilities in different states. Testera's multi-format assessments let us comprehensively evaluate & screen our candidate pool in one go. Managers are far happier with the quality of candidates passing through the initial screening rounds, and we can invest our resources on interested, qualified and relevant candidates.",
      author: "Himank G — Director"
    }
  },
  {
    id: "sales",
    category: "sales",
    industry: "Sales",
    gradient: "from-orange-500 to-yellow-500",
    companyIdentifier: "Enterprise Software Company",
    headline: "Scaling Outreach and Support Teams in 2 Months",
    description: "Enterprise software company needed to rapidly scale outreach teams and customer support executives while maintaining quality. Video scenarios and case-study essays evaluated consultative communication, objection handling, and product aptitude—identifying candidates who could represent the brand professionally from day one.",
    metrics: [
      { label: "Time to hire", value: "3x faster to qualified pool" },
      { label: "Cost per hire", value: "45% reduction" },
      { label: "Volume screened", value: "5x increase in screening volume" }
    ],
    testimonial: {
      quote: "We were spending 15+ hours per week manually reviewing resumes and conducting phone screens for outreach and support roles. Now our AI-generated assessments surface the top 10% of applicants automatically. Our hiring managers only interview pre-qualified candidates.",
      author: "Sapna M — Lead HR"
    }
  }
];

const filters = [
  { id: "all", label: "All Industries" },
  { id: "manufacturing", label: "Manufacturing" },
  { id: "sales", label: "Sales Teams" }
];

export const CaseStudiesShowcase = () => {
  const [activeFilter, setActiveFilter] = useState("all");

  const filteredCaseStudies = activeFilter === "all" 
    ? caseStudies 
    : caseStudies.filter(study => study.category === activeFilter);

  return (
    <section className="py-24 bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-6 lg:px-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Real Results From{" "}
            <span className="text-secondary">High-Volume Hiring Teams</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Companies using Testera to screen 100+ candidates per month
          </p>
        </div>

        {/* Filters */}
        <div className="flex justify-center gap-4 mb-12 flex-wrap">
          {filters.map(filter => (
            <Button
              key={filter.id}
              variant={activeFilter === filter.id ? "default" : "outline"}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-6 py-3 rounded-full transition-all duration-300 ${
                activeFilter === filter.id 
                  ? "bg-gradient-to-r from-primary to-secondary text-white shadow-lg scale-105" 
                  : "bg-muted/60 hover:bg-muted"
              }`}
            >
              {filter.label}
            </Button>
          ))}
        </div>

        {/* Case Study Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {filteredCaseStudies.map((study) => (
            <Card 
              key={study.id}
              className="flex flex-col h-full overflow-hidden group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              {/* Image Header - Fixed Height */}
              <div className="relative h-[240px] overflow-hidden">
                {/* Gradient placeholder */}
                <div className={`w-full h-full bg-gradient-to-br ${study.gradient} transition-transform duration-300 group-hover:scale-105`}></div>
                
                {/* Industry Badge */}
                <Badge className="absolute top-4 left-4 bg-card/95 text-primary shadow-lg border-none">
                  {study.industry}
                </Badge>
              </div>

              {/* Card Content */}
              <CardContent className="flex flex-col flex-1 p-8">
                {/* Company ID */}
                <p className="text-xs text-muted-foreground font-semibold mb-4">
                  {study.companyIdentifier}
                </p>

                {/* Headline - Fixed height with line clamp */}
                <h3 className="text-xl font-bold text-foreground mb-4 h-[84px] leading-tight overflow-hidden line-clamp-3">
                  {study.headline}
                </h3>

                {/* Description - Line clamp for consistency */}
                <p className="text-sm text-muted-foreground mb-6 leading-relaxed line-clamp-4 h-[96px]">
                  {study.description}
                </p>

                {/* Metrics Grid - Fixed minimum height */}
                <div className="bg-muted/50 rounded-lg p-5 mb-6 min-h-[144px]">
                  <div className="space-y-3">
                    {study.metrics.map((metric, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground font-semibold">
                          {metric.label}
                        </span>
                        <span className="text-base font-bold text-primary">
                          {metric.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Testimonial - Fixed height */}
                <blockquote className="border-l-4 border-primary pl-4 mb-6 italic text-xs text-muted-foreground h-[132px] overflow-hidden">
                  <p className="line-clamp-5">
                    "{study.testimonial.quote}"
                  </p>
                  <footer className="not-italic text-xs font-semibold text-foreground mt-2">
                    — {study.testimonial.author}
                  </footer>
                </blockquote>

                {/* Read More - mt-auto pushes to bottom */}
                <a 
                  href="#" 
                  className="inline-flex items-center gap-2 text-primary font-bold hover:gap-3 transition-all mt-auto group/link"
                >
                  Read Full Story
                  <ArrowRight className="h-4 w-4 transition-transform group-hover/link:translate-x-1" />
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
